import * as React from "react";
import { ThemeProvider } from "styled-components";

import Body from "@/components/core-blocks/Body";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import Footer from "@/components/core-blocks/Footer";

import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import Flexible, { IFlexible } from "@/src/components/core-blocks/Flexible";
import GlobalStyle, { MainInner } from "@/styles/global";
import defaultTheme from "@/styles/themes/default.theme";
import { IWPImage } from "../types";

import S from "./page.styled";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { AnimationOnScroll } from "react-animation-on-scroll";

export type IPageTemplate = {
  pageContext: {
    title: string;
    flexible: IFlexible;
    related_cases?: {
      cases: {
        title: string;
        uri: string;
        featuredImage: {
          node: IWPImage;
        };
      }[];
    };
    related_news?: {
      news: {
        title: string;
        uri: string;
        featuredImage: {
          node: IWPImage;
        };
      }[];
    };
    seo: SEOProps;
  };
};

const PageTemplate: React.FC<IPageTemplate> = ({ pageContext }) => {
  const [missingHero, setMissingHero] = React.useState(false);

  React.useEffect(() => {
    if (
      pageContext.flexible.components &&
      pageContext.flexible.components[0].label === "MainHero"
    ) {
      setMissingHero(false);
    } else {
      setMissingHero(true);
    }
  }, []);

  const renderCases = () => {
    if (pageContext.related_cases) {
      return pageContext?.related_cases?.cases?.map((caseItem, i) => {
        const { featuredImage, title, uri } = caseItem;
        const caseImageData = featuredImage?.node?.localFile?.childImageSharp
          ?.gatsbyImageData
          ? getImage(
              featuredImage.node.localFile.childImageSharp.gatsbyImageData
            )
          : null;

        // split title into two at the first .
        const splitTitle = title.split(".");
        const firstTitle = splitTitle[0];
        const secondTitle = splitTitle.slice(1).join(".");
        return (
          <S.CardLink to={uri} key={i}>
            {caseImageData && <GatsbyImage image={caseImageData} alt="" />}
            <S.TitlesWrapper>
              {firstTitle && (
                <S.FirstTitle>
                  {firstTitle}
                  {". "}
                </S.FirstTitle>
              )}
              {secondTitle && <S.SecondTitle>{secondTitle}</S.SecondTitle>}
            </S.TitlesWrapper>
          </S.CardLink>
        );
      });
    }
    return <></>;
  };

  const renderNews = () => {
    if (pageContext.related_news) {
      return pageContext?.related_news?.news?.map((newsItem, i) => {
        const { featuredImage, title, uri } = newsItem;
        const newsImageData = featuredImage?.node?.localFile?.childImageSharp
          ?.gatsbyImageData
          ? getImage(
              featuredImage.node.localFile.childImageSharp.gatsbyImageData
            )
          : null;
        // split title into two at the first .
        const splitTitle = title.split(".");
        const firstTitle = splitTitle[0];
        const secondTitle = splitTitle.slice(1).join(".");
        return (
          <S.CardLink to={uri} key={i}>
            {newsImageData && <GatsbyImage image={newsImageData} alt="" />}
            <S.TitlesWrapper>
              {firstTitle && (
                <S.FirstTitle>
                  {firstTitle}
                  {". "}
                </S.FirstTitle>
              )}
              {secondTitle && <S.SecondTitle>{secondTitle}</S.SecondTitle>}
            </S.TitlesWrapper>
          </S.CardLink>
        );
      });
    }
    return <></>;
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyle />
      <Body>
        <Header />
        <Main>
          <S.Margin missingHero={missingHero} />
          <Flexible components={pageContext.flexible.components} />
          {pageContext.related_cases && (
            <MainInner>
              <S.Container>
                <S.RelatedCasesTitle>Check this out ...</S.RelatedCasesTitle>
                <AnimationOnScroll
                  animateIn="animate__fadeIn"
                  animateOnce={true}
                  initiallyVisible={false}
                >
                  <S.Grid>{renderCases()}</S.Grid>
                </AnimationOnScroll>
              </S.Container>
            </MainInner>
          )}
          {pageContext.related_news && (
            <MainInner>
              <S.Container>
                <S.RelatedCasesTitle>Check this out ...</S.RelatedCasesTitle>
                <AnimationOnScroll
                  animateIn="animate__fadeIn"
                  animateOnce={true}
                  initiallyVisible={false}
                >
                  <S.Grid>{renderNews()}</S.Grid>
                </AnimationOnScroll>
              </S.Container>
            </MainInner>
          )}
          <Footer />
          <CookieConsent />
        </Main>
      </Body>
    </ThemeProvider>
  );
};

export default PageTemplate;

export function Head({ pageContext }: IPageTemplate) {
  return (
    <>
      <html lang="sv" />
      <Seo {...pageContext.seo} lang="sv" />
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css"
      />
    </>
  );
}
